import React, {
  ChangeEvent,
  FocusEvent,
  HTMLAttributes,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import cl from 'classnames';
import { InputMode } from 'src/interface';
import { uuidV4 } from 'src/helper';
import styles from './textareaWithLabelStyle.module.scss';

type TextareaProps = {
  inputMode?: InputMode;
  label: string;
  placeholder?: string;
  value: string;
  error?: string;
  disabled?: boolean;
  className?: string;
  autoSize?: boolean;
  size?: 'small' | 'medium';
  onChange(event: ChangeEvent<HTMLTextAreaElement>): void;
  onFocus?(event: FocusEvent<HTMLTextAreaElement>): void;
  onBlur?(event: FocusEvent<HTMLTextAreaElement>): void;
  tabIndex?: number;
};

export const TextareaWithLabel = ({
  inputMode = 'text',
  label,
  placeholder,
  value,
  error,
  disabled = false,
  className,
  size = 'medium',
  autoSize = false,
  onChange,
  onFocus,
  onBlur,
  tabIndex,
  ...otherProps
}: TextareaProps & HTMLAttributes<HTMLTextAreaElement>) => {
  const uuid = useMemo(uuidV4, []);
  const [isFocused, setFocused] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const handleFocus = useCallback(
    (e: FocusEvent<HTMLTextAreaElement>) => {
      setFocused(true);

      if (onFocus && typeof onFocus === 'function') {
        onFocus(e);
      }
    },
    [onFocus],
  );

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLTextAreaElement>) => {
      setFocused(false);

      if (onBlur && typeof onBlur === 'function') {
        onBlur(e);
      }
    },
    [onBlur],
  );

  return (
    <div className={cl(styles.container, className)}>
      <div
        className={cl(
          styles.inputContainer,
          error && styles.error,
          disabled && styles.disabled,
        )}
      >
        <label
          htmlFor={`textarea-with-label-${uuid}`}
          onClick={(event) => {
            event.preventDefault();
            inputRef.current?.focus();
          }}
          className={cl(
            styles.label,
            Boolean(isFocused || value) && styles.active,
            size === 'small' && styles.small,
            autoSize && styles.autoSize,
            error && styles.error,
          )}
        >
          {label}
        </label>
        <textarea
          id={`textarea-with-label-${uuid}`}
          inputMode={inputMode}
          ref={inputRef}
          className={cl(
            styles.textarea,
            Boolean(isFocused || value) && styles.active,
            size === 'small' && styles.small,
            autoSize && styles.autoSize,
          )}
          value={value}
          disabled={disabled}
          placeholder={isFocused ? placeholder : undefined}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          tabIndex={tabIndex}
          {...otherProps}
        />
      </div>
    </div>
  );
};

export default TextareaWithLabel;
